"use client";

import { Loader2, RotateCcw } from "lucide-react";

export type CommandAgentSettingsPanelId =
    | "general"
    | "behaviour"
    | "tools"
    | "knowledge-base"
    | "llm";

const FIELD_TO_PANEL: Record<string, CommandAgentSettingsPanelId> = {
    name: "general",
    role: "general",
    description: "general",
    visibility: "general",
    shared_roles: "general",
    persona: "behaviour",
    restrictions: "behaviour",
    app_integrations: "tools",
    custom_apps: "tools",
    datasources: "tools",
    data_table_integrations: "tools",
    llm_provider: "llm",
    llm_model: "llm",
    llm_temperature: "llm",
    llm_max_tokens: "llm",
    llm_max_completion_tokens: "llm",
    llm_timeout: "llm",
};

export function getCommandAgentSettingsPanelForField(
    fieldName?: string | null
): CommandAgentSettingsPanelId | undefined {
    if (!fieldName) return undefined;
    return FIELD_TO_PANEL[fieldName];
}

export function getCommandAgentSettingsPanelsWithErrors(
    errors: Record<string, string | undefined | null> | null | undefined
): CommandAgentSettingsPanelId[] {
    const panels = new Set<CommandAgentSettingsPanelId>();
    if (!errors) return [];

    Object.entries(errors).forEach(([key, value]) => {
        if (!value) return;
        const panelId = getCommandAgentSettingsPanelForField(key);
        if (panelId) panels.add(panelId);
    });

    return [...panels];
}

interface CommandAgentSettingsActionBarProps {
    hasUnsavedChanges: boolean;
    isSaving: boolean;
    nameMissing?: boolean;
    onDiscard: () => void;
    onSave: () => void;
    onGoToGeneral?: () => void;
}

export function CommandAgentSettingsActionBar({
    hasUnsavedChanges,
    isSaving,
    nameMissing = false,
    onDiscard,
    onSave,
    onGoToGeneral,
}: CommandAgentSettingsActionBarProps) {
    if (!hasUnsavedChanges) return null;

    return (
        <div className="sticky bottom-0 z-10 mt-auto flex flex-wrap items-center gap-3 rounded-xl border border-border/60 bg-card/95 px-4 py-3 shadow-lg backdrop-blur">
            {nameMissing ? (
                <button
                    type="button"
                    onClick={onGoToGeneral}
                    className="flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                    <span className="size-1.5 rounded-full bg-amber-500" />
                    Name is required
                </button>
            ) : (
                <span className="flex items-center gap-2 text-sm font-medium">
                    <span className="size-1.5 rounded-full bg-orange-500" />
                    Unsaved changes
                </span>
            )}
            <div className="ml-auto flex items-center gap-4">
                <button
                    type="button"
                    onClick={onDiscard}
                    disabled={isSaving}
                    className="inline-flex h-10 items-center gap-1.5 rounded-full border border-border px-4 text-sm font-medium transition-colors hover:bg-muted disabled:opacity-50"
                >
                    <RotateCcw className="size-4" />
                    Discard
                </button>
                <button
                    type="button"
                    onClick={onSave}
                    disabled={nameMissing || isSaving}
                    className="inline-flex h-10 items-center gap-1.5 rounded-full bg-primary px-6 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                >
                    {isSaving ? (
                        <Loader2 className="size-4 animate-spin" aria-hidden />
                    ) : null}
                    {isSaving ? "Saving…" : "Save changes"}
                </button>
            </div>
        </div>
    );
}
